import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatoMXN, formatoFecha, METODOS_PAGO } from '@/lib/api';
import { Printer, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReciboImprimir() {
  const { id, pagoId } = useParams();
  const navigate = useNavigate();
  const [orden, setOrden] = useState(null);
  const [pago, setPago] = useState(null);
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const [oRes, cRes] = await Promise.all([
          api.get(`/ordenes/${id}`),
          api.get('/configuracion')
        ]);
        setOrden(oRes.data);
        setConfig(cRes.data);
        const pagoEncontrado = oRes.data.pagos?.find(p => p.id === pagoId);
        setPago(pagoEncontrado || null);
      } catch {
        navigate(`/ordenes/${id}`);
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [id, pagoId, navigate]);

  if (cargando) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!orden || !pago || !config) return null;

  const metodoLabel = METODOS_PAGO.find(m => m.valor === pago.metodo_pago)?.label || pago.metodo_pago;

  return (
    <div className="min-h-screen bg-white" data-testid="recibo-imprimir-page">
      {/* Barra de acciones */}
      <div className="print-hide sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/ordenes/${id}`)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="volver-orden-recibo-btn">
          <ArrowLeft className="w-4 h-4" /> Volver a la orden
        </button>
        <button onClick={() => window.print()} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-sm text-sm font-bold transition-colors" data-testid="imprimir-recibo-btn">
          <Printer className="w-4 h-4" /> Imprimir Recibo
        </button>
      </div>

      {/* Contenido imprimible */}
      <div className="max-w-[600px] mx-auto p-8 print-content" style={{ fontFamily: 'Inter, sans-serif', color: '#0f172a' }}>
        {/* Encabezado */}
        <div className="text-center border-b-2 border-slate-900 pb-4 mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            {config.logo_url ? (
              <img src={`${API_URL}${config.logo_url}?v=${encodeURIComponent(config.logo_actualizado_en || '')}`} alt="Logo" className="w-20 h-20 object-contain" data-testid="recibo-print-logo" />
            ) : (
              <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
              </div>
            )}
            <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '22px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {config.nombre_taller}
            </h1>
          </div>
          <p className="text-xs text-slate-500">{config.direccion}</p>
          <p className="text-xs text-slate-500">
            Tel: {config.telefono} | {config.email}
            {config.rfc && <> | RFC: <span className="font-mono">{config.rfc}</span></>}
          </p>
        </div>

        {/* Titulo del recibo */}
        <div className="text-center mb-8">
          <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '26px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Recibo de Pago
          </h2>
          <p className="text-xs text-slate-400 font-mono mt-1">
            ID: {pago.id.substring(0, 8).toUpperCase()}
          </p>
        </div>

        {/* Datos del recibo */}
        <div className="border border-slate-200 rounded-sm mb-6">
          <div className="grid grid-cols-2">
            <div className="p-4 border-b border-r border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Fecha de Pago</p>
              <p className="text-sm font-semibold mt-1">{formatoFecha(pago.fecha_pago)}</p>
            </div>
            <div className="p-4 border-b border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Orden de Trabajo</p>
              <p className="text-sm font-semibold mt-1 font-mono" style={{ color: '#f59e0b' }}>{orden.numero_orden}</p>
            </div>
            <div className="p-4 border-b border-r border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Cliente</p>
              <p className="text-sm font-semibold mt-1">{orden.cliente_nombre}</p>
            </div>
            <div className="p-4 border-b border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Vehículo</p>
              <p className="text-sm mt-1">{orden.vehiculo_info || '-'}</p>
            </div>
            <div className="p-4 border-r border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Método de Pago</p>
              <p className="text-sm font-semibold mt-1 capitalize">{metodoLabel}</p>
            </div>
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Referencia</p>
              <p className="text-sm mt-1 font-mono">{pago.referencia || '-'}</p>
            </div>
          </div>
        </div>

        {/* Monto del pago */}
        <div className="bg-slate-50 border-2 border-slate-900 rounded-sm p-6 mb-6 text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Monto del Pago</p>
          <p className="font-mono font-bold" style={{ fontSize: '36px', color: '#0f172a' }}>
            {formatoMXN(pago.monto)}
          </p>
          <p className="text-xs text-slate-400 mt-1">({montoEnLetras(pago.monto)})</p>
        </div>

        {/* Resumen de la orden */}
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3 border-b border-slate-300 pb-2">
            Resumen de la Orden
          </p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs py-1">
              <span className="text-slate-600">Total de la orden</span>
              <span className="font-mono font-semibold">{formatoMXN(orden.total)}</span>
            </div>
            <div className="flex justify-between text-xs py-1">
              <span className="text-slate-600">Total pagado a la fecha</span>
              <span className="font-mono font-semibold text-green-700">{formatoMXN(orden.total_pagado)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-t border-slate-200 font-bold">
              <span className={orden.saldo_pendiente > 0 ? 'text-red-700' : 'text-green-700'}>
                Saldo pendiente
              </span>
              <span className={`font-mono ${orden.saldo_pendiente > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {formatoMXN(orden.saldo_pendiente)}
              </span>
            </div>
          </div>
        </div>

        {pago.notas && (
          <div className="mb-6">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Notas</p>
            <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-sm border border-slate-200">{pago.notas}</p>
          </div>
        )}

        {/* Firmas */}
        <div className="grid grid-cols-2 gap-12 mt-12 pt-4">
          <div className="text-center">
            <div className="border-t border-slate-400 pt-2 mt-12">
              <p className="text-xs text-slate-500">Recibi conforme</p>
              <p className="text-xs text-slate-400 mt-1">{config.nombre_taller}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-slate-400 pt-2 mt-12">
              <p className="text-xs text-slate-500">Firma del Cliente</p>
              <p className="text-xs text-slate-400 mt-1">{orden.cliente_nombre}</p>
            </div>
          </div>
        </div>

        {/* Pie */}
        <div className="mt-8 pt-4 border-t border-slate-200 text-center">
          <p className="text-[10px] text-slate-400">
            {config.nombre_taller} | {config.direccion} | Tel: {config.telefono}
          </p>
          <p className="text-[10px] text-slate-400 mt-1">
            Documento generado el {new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
}

function montoEnLetras(monto) {
  const entero = Math.floor(monto);
  const centavos = Math.round((monto - entero) * 100);
  const unidades = ['', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
  const decenas = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
  const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciseis', 'diecisiete', 'dieciocho', 'diecinueve'];
  const centenas = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

  if (entero === 0) return `cero pesos ${centavos}/100 M.N.`;

  const convertir = (n) => {
    if (n === 0) return '';
    if (n === 100) return 'cien';
    if (n < 10) return unidades[n];
    if (n < 20) return especiales[n - 10];
    if (n < 100) {
      const d = Math.floor(n / 10);
      const u = n % 10;
      if (u === 0) return decenas[d];
      if (d === 2) return `veinti${unidades[u]}`;
      return `${decenas[d]} y ${unidades[u]}`;
    }
    if (n < 1000) {
      const c = Math.floor(n / 100);
      const resto = n % 100;
      return `${centenas[c]}${resto > 0 ? ' ' + convertir(resto) : ''}`;
    }
    if (n < 1000000) {
      const miles = Math.floor(n / 1000);
      const resto = n % 1000;
      const milesStr = miles === 1 ? 'mil' : `${convertir(miles)} mil`;
      return `${milesStr}${resto > 0 ? ' ' + convertir(resto) : ''}`;
    }
    return String(n);
  };

  return `${convertir(entero)} pesos ${centavos}/100 M.N.`;
}
